import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { http } from '@/lib/http';

// `GET /v1/cases/:caseUrn/costs` — the route keeps its name, but every amount is the
// airline's PRICE: operator-bff prices each reservation with the commercial contract's
// price card before it leaves the server (commercial-contract §6.13, slice P4). No
// price card → the amounts are `null` and `priced: false` («No disponible», never 0).

export interface CostLine {
  hotelUrn: string;
  hotelName: string;
  /** Live reservations at this hotel (BOOKED + held). */
  reservations: number;
  /** Rooms across those reservations; null when booking did not record a room count. */
  rooms: number | null;
  /** Committed (BOOKED) price at this hotel; null when not priced. */
  bookedUSD: number | null;
  /** Held (PENDING) price at this hotel; null when not priced. */
  heldUSD: number | null;
  /** Per-room price when every reservation at the hotel carries the same one; else null. */
  contractRateUSD: number | null;
}

export interface CostsSummary {
  caseUrn: string;
  /** Committed — Σ the BOOKED lines' price; null when not priced. */
  totalContractUSD: number | null;
  /** Held — Σ the PENDING / HELD lines' price; null when not priced. */
  totalHeldUSD: number | null;
  /** No market-rate source exists in the platform yet — always null (never a synthetic figure). */
  totalMarketUSD: number | null;
  savingsUSD: number | null;
  /** No allowance/budget source is wired yet — null (not "0 issued"). */
  allowanceUSD: number | null;
  currency: string;
  lines: CostLine[];
  sources?: { booking: 'ok' | 'unavailable' };
  /** `false` → the BFF could not read the price card; every amount above is null. */
  priced?: boolean;
}

const KEY = (caseUrn: string) => ['cases', caseUrn, 'costs'] as const;

export function useCosts(caseUrn: string) {
  return useQuery<CostsSummary>({
    queryKey: KEY(caseUrn),
    queryFn: () =>
      http.get(`v1/cases/${encodeURIComponent(caseUrn)}/costs`).json<CostsSummary>(),
    staleTime: 60_000,
    enabled: !!caseUrn,
  });
}

interface IssueAllowanceRequest {
  amountUSD: number;
  reason: string;
}

export function useIssueAllowance(caseUrn: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: IssueAllowanceRequest) =>
      http
        .post(`v1/cases/${encodeURIComponent(caseUrn)}/allowance`, { json: input })
        .json<CostsSummary>(),
    onSuccess: (next) => qc.setQueryData(KEY(caseUrn), next),
  });
}
